import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { LocalTaxMode, TaxesDTO, TaxEstimationMode } from '../../api/useTaxesQuery';
import { Select } from '../../../../CommonComponents/OPL/Select/Select/Select';
import { Box } from '../../../../CommonComponents/OPL/Box/Box';
import { InputNumberMask } from '../../../../CommonComponents/OPL/Input/InputNumberMask';
import { Radio } from '../../../../CommonComponents/OPL/Radio/Radio';
import { Text } from '../../../../CommonComponents/OPL/Text/Text';
import { FormValues, NEW_YORK_STATE, TaxesFormVariant } from '../../Taxes.meta';

interface Props {
  variant?: TaxesFormVariant;
  data: TaxesDTO;
}

const FIELD_NAMES = {
  base: {
    taxEstimationMode: 'preRetirementTaxEstimationMode',
    localTaxMode: 'preRetirementLocalTaxMode',
    percentageLocalTax: 'preRetirementPercentageLocalTax',
    flatLocalTax: 'preRetirementFlatLocalTax',
    effectiveTaxRate: 'preRetirementEffectiveTaxRate',
  },
  preRetirementOverride: {
    taxEstimationMode: 'preRetirementOverrideTaxEstimationMode',
    localTaxMode: 'preRetirementOverrideLocalTaxMode',
    percentageLocalTax: 'preRetirementOverridePercentageLocalTax',
    flatLocalTax: 'preRetirementOverrideFlatLocalTax',
    effectiveTaxRate: 'preRetirementOverrideEffectiveTaxRate',
    selectedFilingState: 'preRetirementSelectedFilingState',
  },
  postRetirement: {
    taxEstimationMode: 'postRetirementTaxEstimationMode',
    localTaxMode: 'postRetirementLocalTaxMode',
    percentageLocalTax: 'postRetirementPercentageLocalTax',
    flatLocalTax: 'postRetirementFlatLocalTax',
    effectiveTaxRate: 'postRetirementEffectiveTaxRate',
    selectedFilingState: 'postRetirementSelectedFilingState',
  },
} as const;

export const TaxesForm = ({ variant = 'base', data }: Props) => {
  const formBag = useFormContext<FormValues>();
  const fields = FIELD_NAMES[variant];

  const selectedFilingState =
    variant === 'postRetirement'
      ? formBag.watch('postRetirementSelectedFilingState')
      : variant === 'preRetirementOverride'
        ? formBag.watch('preRetirementSelectedFilingState')
        : null;

  const taxEstimationMode = formBag.watch(fields.taxEstimationMode);
  const localTaxMode = formBag.watch(fields.localTaxMode);

  const localTaxModeOptions =
    variant === 'postRetirement'
      ? data.postRetirementLocalTaxOptions
      : variant === 'preRetirementOverride'
        ? data.preRetirementLocalTaxOptions
        : data.localTaxModeOptions;

  const filteredLocalTaxModeOptions =
    selectedFilingState === null || selectedFilingState === NEW_YORK_STATE
      ? localTaxModeOptions
      : localTaxModeOptions.filter(({ value }) => value !== LocalTaxMode.NewYorkCityBrackets);

  const stateOptions = [
    {
      label: 'United States',
      options: data.filingStateOptions.filter(({ IsInsideUS }) => IsInsideUS),
    },
    {
      label: 'Outside U.S.',
      options: data.filingStateOptions.filter(({ IsInsideUS }) => !IsInsideUS),
    },
  ];

  const radioGroupSuffix =
    variant === 'postRetirement' ? 'postRetirement' : variant === 'preRetirementOverride' ? 'preRetirementOverride' : '';

  return (
    <Box maxWidth={400}>
      {!data.canUseEffectiveTaxRateOnly && (
        <Controller
          name={fields.taxEstimationMode}
          control={formBag.control}
          render={({ field: estimationModeField }) => (
            <Box
              role="radiogroup"
              display="flex"
              flexDirection="column"
              aria-labelledby={`tax-estimation-mode--radio-group${radioGroupSuffix}`}
              style={{ gap: 8 }}
            >
              <Text
                id={`tax-estimation-mode--radio-group${radioGroupSuffix}`}
                as="p"
                variant="formLabel"
                color="neutral.600"
              >
                Select Tax Rate Method
              </Text>

              <Radio
                name={estimationModeField.name}
                value={TaxEstimationMode.Automatic}
                checked={estimationModeField.value === TaxEstimationMode.Automatic}
                onChange={() => {
                  estimationModeField.onChange(TaxEstimationMode.Automatic);
                }}
                disabled={estimationModeField.disabled}
              >
                Automatically estimate taxes
              </Radio>

              <Radio
                name={estimationModeField.name}
                value={TaxEstimationMode.UseEffectiveTaxRate}
                checked={estimationModeField.value === TaxEstimationMode.UseEffectiveTaxRate}
                onChange={() => {
                  estimationModeField.onChange(TaxEstimationMode.UseEffectiveTaxRate);
                }}
                disabled={estimationModeField.disabled}
              >
                Use an effective tax rate
              </Radio>
            </Box>
          )}
        />
      )}

      {taxEstimationMode === TaxEstimationMode.Automatic && (
        <Box display="flex" flexDirection="column" style={{ gap: 8 }} mt="4">
          {variant !== 'base' && 'selectedFilingState' in fields && (
            <Controller
              control={formBag.control}
              name={fields.selectedFilingState}
              render={({ field }) => (
                <Select
                  {...field}
                  label="Filing State"
                  value={stateOptions
                    .flatMap(({ options }) => options)
                    .find(({ value }) => value === field.value)}
                  options={stateOptions}
                  onChange={opt => {
                    if (opt) {
                      field.onChange(opt.value);
                      formBag.setValue(fields.localTaxMode, LocalTaxMode.No);
                    }
                  }}
                />
              )}
            />
          )}

          <Controller
            control={formBag.control}
            name={fields.localTaxMode}
            render={({ field }) => (
              <Select
                {...field}
                label="Local Tax"
                value={filteredLocalTaxModeOptions.find(({ value }) => value === field.value)}
                options={filteredLocalTaxModeOptions}
                onChange={opt => {
                  if (opt) {
                    field.onChange(opt.value);
                  }
                }}
              />
            )}
          />

          <Box display="flex" css="gap: 8px">
            {[LocalTaxMode.PercentageOfIncome, LocalTaxMode.PercentageOfStateTax].includes(
              localTaxMode,
            ) && (
              <Box flex={1} flexShrink={0}>
                <Controller
                  control={formBag.control}
                  name={fields.percentageLocalTax}
                  render={({ field: { onChange, ...field } }) => (
                    <InputNumberMask
                      {...field}
                      label={
                        localTaxMode === LocalTaxMode.PercentageOfIncome
                          ? 'Percentage of Income'
                          : 'Percentage of State Taxes'
                      }
                      allowNegative={false}
                      rightIcon="far fa-percent"
                      onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
                    />
                  )}
                />
              </Box>
            )}

            {localTaxMode !== LocalTaxMode.No && (
              <Box flex={1} flexShrink={0}>
                <Controller
                  control={formBag.control}
                  name={fields.flatLocalTax}
                  render={({ field: { onChange, ...field } }) => (
                    <InputNumberMask
                      {...field}
                      label={`${
                        localTaxMode !== LocalTaxMode.FlatAmount ? 'Additional ' : ''
                      }Annual Local Taxes`}
                      leftIcon="fa fa-dollar-sign"
                      allowNegative={false}
                      onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
                    />
                  )}
                />
              </Box>
            )}
          </Box>
        </Box>
      )}

      {taxEstimationMode === TaxEstimationMode.UseEffectiveTaxRate && (
        <Box pl={28} mt="4">
          <Controller
            control={formBag.control}
            name={fields.effectiveTaxRate}
            render={({ field: { onChange, ...field } }) => (
              <InputNumberMask
                {...field}
                aria-label="Effective tax rate"
                allowNegative={false}
                rightIcon="far fa-percent"
                onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
              />
            )}
          />
        </Box>
      )}
    </Box>
  );
};
